import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useGetTaskQuery } from '@/modules/common/queries/tasks.ts';
import {
  TaskFormValues,
  taskValidationSchema,
} from '@/modules/admin-dashboard/components/modals/CreateTaskModal.tsx';
import { TaskModal } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import { sanitizeTaskReminders } from '@/modules/admin-dashboard/helpers/sanitizeTaskReminders.ts';
import { useUpdateTaskMutation } from '@/modules/admin-dashboard/queries/tasks.ts';
import { Avatar, BaseModalProps } from 'ui';
import { TaskPriority, TaskWithRelationsResponse, User } from 'compliance-api';

interface UpdateTaskModalProps extends BaseModalProps {
  id: number;
  defaultDisabled?: boolean;
  clientId?: number;
}

const initialValues: TaskFormValues = {
  name: '',
  priority: TaskPriority.Normal,
  start_date: '',
  first_due_date: '',
  recurrency_rule: '',
  user_ids: [],
  users_overdue_subs: [],
  users_completed_subs: [],
  department_ids: [],
  is_every_employee_needs_to_complete: false,
  form_id: 0,
  description: '',
  due_remainders: [],
  incident_result_id: undefined,
  client_id: undefined,
  preservedFullOptions: {
    department_ids: [],
    user_ids: [],
    users_completed_subs: [],
    users_overdue_subs: [],
    client_id: undefined,
  },
};

export const createOptionsFromUser = (users: User[]) => {
  return users.map(item => {
    return {
      value: item.id,
      label: item?.first_name + ' ' + item?.last_name,
      avatar: (
        <Avatar
          withBorder
          size="sm"
          className="border-[#C4C4C4]"
          classNameLetters="!text-[12px]"
          src={item?.avatar_url}
          firstName={item?.first_name}
          lastName={item?.last_name}
        />
      ),
    };
  });
};

export const UpdateTaskModal = ({
  id,
  open,
  onClose,
  defaultDisabled = false,
  clientId,
}: UpdateTaskModalProps) => {
  const { data: task, isLoading } = useGetTaskQuery(id);
  const [disabledMode, setDisabledMode] = useState(defaultDisabled);

  const { mutateAsync: updateTask, isLoading: isUpdating } = useUpdateTaskMutation();

  const handleCloseModal = () => {
    formik.resetForm();
    onClose();
  };

  const handleSubmit = useCallback(
    async (values: TaskFormValues) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { preservedFullOptions, ...taskData } = values;
      await updateTask(sanitizeTaskReminders({ ...taskData, id }));
    },
    [id, updateTask],
  );

  const formik = useFormik({
    initialValues: {
      ...initialValues,
    },
    validationSchema: taskValidationSchema,
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    enableReinitialize: true,

    onSubmit: async values => {
      await handleSubmit(values);
      handleCloseModal();
    },
  });

  useEffect(() => {
    if (task) {
      formik.setFieldValue('name', task?.task.name);
      formik.setFieldValue('priority', task?.task.priority);
      formik.setFieldValue('start_date', task?.task.start_date);
      formik.setFieldValue('first_due_date', task?.task.first_due_date);
      formik.setFieldValue('recurrency_rule', task?.task.recurrency_rule);
      formik.setFieldValue(
        'user_ids',
        task?.task.users ? task?.task.users.map(item => item.id) : [],
      );
      formik.setFieldValue(
        'users_overdue_subs',
        task?.task.overdue_subs ? task?.task.overdue_subs.map(item => item.id) : [],
      );
      formik.setFieldValue(
        'users_completed_subs',
        task?.task.complete_subs ? task?.task.complete_subs.map(item => item.id) : [],
      );
      formik.setFieldValue(
        'department_ids',
        task?.task.departments ? task.task.departments.map(item => item.id) : [],
      );
      formik.setFieldValue(
        'is_every_employee_needs_to_complete',
        task?.task.is_every_employee_needs_to_complete,
      );
      formik.setFieldValue('form_id', task?.task.form_id);
      formik.setFieldValue('incident_result_id', task?.task.incident_result_id);
      formik.setFieldValue('description', task?.task.description);
      formik.setFieldValue('due_remainders', task?.task.due_remainders);

      // Use clientId from page context if provided, otherwise use task's client.id
      const taskData = task.task as TaskWithRelationsResponse & { client_id?: number };
      const effectiveClientId = clientId ?? taskData.client?.id;
      // Determine client option for the select
      let clientOption;
      if (taskData.client) {
        clientOption = {
          value: taskData.client.id,
          label: taskData.client.name,
        };
      } else {
        clientOption = undefined;
      }

      formik.setFieldValue('preservedFullOptions', {
        department_ids: (task?.task?.departments || []).map(item => {
          return { value: item.id, label: item.name };
        }),
        user_ids: createOptionsFromUser(task?.task?.users || []),
        users_completed_subs: createOptionsFromUser(task?.task?.complete_subs || []),
        users_overdue_subs: createOptionsFromUser(task?.task?.overdue_subs || []),
        client_id: clientOption,
      });

      // Set client_id AFTER preservedFullOptions to ensure it's not overwritten
      formik.setFieldValue('client_id', effectiveClientId);
    }
  }, [task, clientId]);

  return (
    <TaskModal
      isUpdating
      defaultForm={
        task?.task.form ? { value: task?.task.form.id, label: task?.task.form.name } : null
      }
      defaultIncident={
        task?.task.incident_result
          ? { value: { id: task?.task.incident_result.id }, label: task?.task.incident_result.name }
          : null
      }
      disabledMode={disabledMode}
      setDisabledMode={setDisabledMode}
      isEveryBodyNeedsToBeCompleted={task?.task.is_every_employee_needs_to_complete}
      isTaskDataLoading={isLoading}
      isTaskLoading={isUpdating}
      formik={formik}
      handleCloseModal={handleCloseModal}
      isOpened={open}
      clientId={clientId}
    />
  );
};
